import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, 
  LogOut,
  Menu,
  X,
  Sparkles
} from 'lucide-react';

const StylistLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isAuthenticated = localStorage.getItem('stylistToken');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/stylist/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('stylistToken');
    navigate('/stylist/login');
  };

  const navItems = [
    { to: '/stylist', icon: Users, label: 'Клиенты', exact: true },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh',
      backgroundColor: 'var(--cream)'
    }}>
      {/* Sidebar - Desktop */}
      <aside style={{
        width: '260px',
        backgroundColor: 'var(--charcoal)',
        display: 'none',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 100
      }}
      className="sidebar-desktop"
      >
        {/* Logo */}
        <div style={{
          padding: 'var(--space-xl)',
          borderBottom: '1px solid var(--dark-gray)'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-s)' }}>
              <Sparkles size={20} style={{ color: 'var(--accent-gold)' }} />
              <span style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.125rem',
                color: 'var(--white)',
                fontWeight: 300
              }}>
                Панель стилиста
              </span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: 'var(--space-l) 0' }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to, item.exact);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`stylist-nav-${item.label.toLowerCase()}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-m)',
                  padding: 'var(--space-m) var(--space-xl)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: active ? 'var(--white)' : '#A0A0A0',
                  textDecoration: 'none',
                  backgroundColor: active ? 'var(--dark-gray)' : 'transparent',
                  borderLeft: active ? '3px solid var(--accent-gold)' : '3px solid transparent',
                  transition: 'all 200ms ease'
                }}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div style={{
          padding: 'var(--space-l) var(--space-xl)',
          borderTop: '1px solid var(--dark-gray)'
        }}>
          <button
            onClick={handleLogout}
            data-testid="stylist-logout-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              background: 'none',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              color: '#A0A0A0',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <LogOut size={16} />
            Выйти
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        backgroundColor: 'var(--charcoal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 var(--space-l)',
        zIndex: 200
      }}
      className="mobile-header"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-s)' }}>
          <Sparkles size={18} style={{ color: 'var(--accent-gold)' }} />
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1rem',
            color: 'var(--white)'
          }}>
            Панель стилиста
          </span>
        </div>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--space-s)',
            color: 'var(--white)'
          }}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div style={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--charcoal)',
          zIndex: 150,
          overflowY: 'auto'
        }}
        className="mobile-menu"
        >
          <nav style={{ padding: 'var(--space-m) 0' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to, item.exact);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-m)',
                    padding: 'var(--space-l) var(--space-xl)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '1rem',
                    color: active ? 'var(--white)' : '#A0A0A0',
                    textDecoration: 'none',
                    backgroundColor: active ? 'var(--dark-gray)' : 'transparent',
                    borderBottom: '1px solid var(--dark-gray)'
                  }}
                >
                  <Icon size={22} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div style={{ padding: 'var(--space-xl)' }}>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                background: 'none',
                border: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: '1rem',
                color: '#A0A0A0',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <LogOut size={18} />
              Выйти
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main style={{
        flex: 1,
        marginLeft: 0,
        marginTop: '60px',
        padding: 'var(--space-l)',
        minHeight: 'calc(100vh - 60px)'
      }}
      className="main-content"
      >
        {children}
      </main>

      <style>{`
        @media (min-width: 769px) {
          .sidebar-desktop {
            display: flex !important;
          }
          .mobile-header {
            display: none !important;
          }
          .mobile-menu {
            display: none !important;
          }
          .main-content {
            margin-left: 260px !important;
            margin-top: 0 !important;
            padding: var(--space-xxl) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StylistLayout;
